import { api1 } from "../utils/axiosInstance";
import { API_ENDPOINTS } from "./apiEndpoints";

export const getMenteesFromProfile = async (mentorId) => {
  try {
    const [mentorRes, messagesRes] = await Promise.all([
      api1.get(`${API_ENDPOINTS.GET_MENTOR_PROFILE}/${mentorId}`),
      api1.get(`${API_ENDPOINTS.GET_CONVERSATIONS_LATEST_MESSAGE}/${mentorId}`)
    ]);

    const mentees = mentorRes.data.data?.mentees || [];
    const messages = messagesRes.data.data || [];

    if (!mentees.length) return [];

    const messagesByMentee = {};
    messages.forEach(({ conversationId, lastMessage, lastMessageAt }) => {
      const [idA, idB] = conversationId.split('_');
      const menteeId = idA === mentorId ? idB : idA;
      messagesByMentee[menteeId] = { lastMessage, lastMessageAt };
    });

    const menteesData = mentees.map(({ _id, profile }) => {
      const message = messagesByMentee[_id] || {};
      const fullName = `${profile?.firstName || ""} ${profile?.lastName || ""}`.trim() || "Unknown Mentee";
      const messageTime = new Date(message.lastMessageAt || 0);

      return {
        _id,
        name: fullName,
        profile: profile?.profilePhoto || null,
        lastMessage: message.lastMessage || "No messages yet",
        lastMessageTime: messageTime,
        time: formatTime(message.lastMessageAt || messageTime),
      };
    });

    return menteesData.sort((a, b) => b.lastMessageTime - a.lastMessageTime);
  } catch (err) {
    console.error("Failed to fetch mentees:", err);
    return [];
  }
};


export const getMentorsFromProfile = async (menteeId) => {
  try {
    const userResponse = await api1.get(`${API_ENDPOINTS.GET_SINGLE_PROFILE}/${menteeId}`);
    const userProfile = userResponse.data.data || userResponse.data;
    
    if (!userProfile?.mentors?.length) return [];

    const mentorProfilePromises = userProfile.mentors.map(mentor => 
      api1.get(`${API_ENDPOINTS.GET_MENTOR_PROFILE}/${mentor._id}`)
        .then(response => ({ mentorId: mentor._id, profile: response.data.data }))
        .catch(() => ({ mentorId: mentor._id, profile: null }))
    );

    const [mentorProfiles, conversationsRes] = await Promise.all([
      Promise.all(mentorProfilePromises),
      api1.get(`${API_ENDPOINTS.GET_CONVERSATIONS_LATEST_MESSAGE}/${menteeId}`)
    ]);
    
    const conversations = conversationsRes.data.data || [];
    
    const mentorProfileMap = {};
    mentorProfiles.forEach(({ mentorId, profile }) => {
      mentorProfileMap[mentorId] = profile;
    });

    const conversationMap = {};
    conversations.forEach(conv => {
      const [id1, id2] = conv.conversationId.split('_');
      const mentorId = id1 === menteeId ? id2 : id1;
      conversationMap[mentorId] = conv;
    });

    return userProfile.mentors.map((mentor) => {
      const mentorId = mentor._id;
      const mentorProfile = mentorProfileMap[mentorId];
      const conversation = conversationMap[mentorId];
      
      const lastMessageData = conversation
        ? {
            text: conversation.lastMessage || "No messages yet",
            time: new Date(conversation.lastMessageAt || 0),
            timestamp: conversation.lastMessageAt
          }
        : {
            text: "No messages yet",
            time: new Date(0),
            timestamp: null
          };
      
      return {
        _id: mentorId,
        name: mentorProfile 
          ? `${mentorProfile?.firstName || ""} ${mentorProfile?.lastName || ""}`.trim() || "Unknown Mentor"
          : "Unknown Mentor",
        profile: mentorProfile?.profileImage || null,
        lastMessage: lastMessageData.text,
        lastMessageTime: lastMessageData.time,
        time: formatTime(lastMessageData.timestamp || lastMessageData.time),
      };
    }).sort((a, b) => b.lastMessageTime - a.lastMessageTime);
  } catch (error) {
    console.error("Error fetching mentors from profile:", error);
    return [];
  }
};

const formatTime = (date) => {
  const diff = (new Date() - new Date(date)) / 1000;
  if (diff < 10) return "Just now";
  if (diff < 60) return `${Math.floor(diff)}s ago`;
  if (diff < 3600) return `${Math.floor(diff / 60)}m ago`;
  if (diff < 86400) return `${Math.floor(diff / 3600)}h ago`;
  return new Date(date).toLocaleDateString("en-US", { month: "short", day: "numeric" });
};
